import { isBauhofOrder, formatOrderDeadline } from "@/shared/orders/orderDeadlines";
import React, { Fragment, useCallback, useEffect, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import useApiService from "@/api/ApiService";

import OrderKindStep, { OrderKind } from "./steps/OrderKindStep";
import MaterialStep from "./steps/MaterialStep";
import BauhofStep from "./steps/BauhofStep";
import DetailsStep, { EditorMode, EditSection } from "./steps/DetailsStep";
import MengeStep from "./steps/MengeStep";
import UhrzeitStep from "./steps/UhrzeitStep";
import SonstigesStep from "./steps/SonstigesStep";

import { AllowScrollProvider, useAllowScroll } from "./AllowScrollContext"; // Import context
import FuhrparkStep from "./steps/FuhrparkStep";
import HistoryStep from "./steps/HistoryStep";
import { MaterialOrder } from "../../Project";
import { useGlobalStore, usePersistantStore } from "@/stores/global";
import ApiService from "@/api/ApiService";
import { Button, CircularProgress } from "@mui/material";
import StyledModal from "./StyledModal";
import { Psychology, SentimentSatisfied } from "@mui/icons-material";
import { Brain, Smile } from "lucide-react";
import useRights from "@/shared/api/useRights";

type WizardStep = "KIND" | "MATERIAL" | "HISTORY" | "BAUHOF" | "MENGE" | "UHRZEIT" | "FUHRPARK" | "SONSTIGES" | "DETAILS";

function filterKeinTransport(state: MaterialOrder): MaterialOrder {
  return {
    ...state,
    transportInformation: state.transportInformation?.filter((t) => (t.amount ?? 0) !== 0 && t.type !== "XX") ?? null,
  };
}

function MaterialEditor({
  Opener,
  initialState,
  bauhofOnly = false,
  bauhofDeadline,
  projectId,
  manualPosition = null,
  jobId,
  invalidate,
  saveType = "CALLBACK",
  onSave,
}: {
  Opener: ({ onClick }: { onClick: any }) => JSX.Element;
  initialState: MaterialOrder;
  bauhofOnly?: boolean;
  bauhofDeadline?: string;
  manualPosition: any;
  projectId: number;
  jobId: number;
  saveType: "DIRECT" | "CALLBACK";
  invalidate?: () => void;
  onSave?: (mo: MaterialOrder) => void;
}) {
  const {
    allowScroll,
    setAllowScroll,
    setView,
    selectedOrderId,
    setSelectedOrderId,
    selectedPlantKey,
    setSelectedPlantKey,
    autoPlantSelected,
    setAutoPlantSelected,
  } = useAllowScroll(); // Use the context
  const queryClient = useQueryClient();

  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<MaterialOrder>(initialState);
  const [currentStep, setCurrentStep] = useState<WizardStep>(initialState.id === 0 ? (bauhofOnly ? "BAUHOF" : "KIND") : "DETAILS");
  const [orderKind, setOrderKind] = useState<OrderKind | null>(null);
  const [editingSection, setEditingSection] = useState<EditSection | null>(null);

  const region = usePersistantStore((s) => s.region);
  const userId = usePersistantStore((s) => s.userId);

  const rights = useRights();
  const isDispo = "TRANSPORTDISPO" in rights || "DISPOSITION" in rights;

  const expertMode = usePersistantStore((state) => state.getSettings().expertMode);
  const setSettings = usePersistantStore((s) => s.setSettings);

  const mode: EditorMode = initialState.id === 0 ? "create" : "edit"; // Added mode

  const mutation =
    saveType === "DIRECT"
      ? useMutation({
          mutationFn: order.id > 0 ? ApiService.editEntity("materialOrder", order.id, "app") : ApiService.createEntity("materialOrder", { v: "app" }),
          onSuccess: () => {
            !!invalidate ? invalidate() : null;
            close();
          },
        })
      : {
          isPending: false,
          mutate: (mo: MaterialOrder) => {
            if (!!onSave) onSave(mo);
            close();
          },
        };

  const open = useCallback(() => {
    setLoading(true);
    setOrder({ ...initialState });
    setOrderKind(null);
    setEditingSection(null);
    setCurrentStep(initialState.id === 0 ? (bauhofOnly ? "BAUHOF" : "KIND") : "DETAILS");
    setModalVisible(true);
  }, [initialState, bauhofOnly]);

  useEffect(() => {
    if (currentStep !== "MATERIAL") {
      setAllowScroll(false);
      setView("ORDER");
      setSelectedOrderId(null);
      setSelectedPlantKey(null);
      setAutoPlantSelected(false);
    }
  }, [currentStep, setAllowScroll, setView, setSelectedOrderId, setSelectedPlantKey, setAutoPlantSelected]);

  useEffect(() => {
    if (bauhofOnly && ["KIND", "MATERIAL", "HISTORY"].includes(currentStep)) setCurrentStep("BAUHOF");
  }, [bauhofOnly, currentStep]);

  const close = useCallback(() => {
    console.log("CLOSE CALLED");
    setModalVisible(false);
  }, []);

  const handleSave = useCallback(
    (updatedOrder: MaterialOrder) => {
      if (bauhofOnly && !isBauhofOrder(updatedOrder)) return;
      setOrder(updatedOrder);
      mutation.mutate(updatedOrder);
    },
    [mutation, bauhofOnly],
  );

  const handleSendNow = useCallback(() => {
    if (bauhofOnly && !isBauhofOrder(order)) return;
    const prepared = filterKeinTransport({
      ...order,
      orderSentAt: dayjs.utc().format("YYYY-MM-DD HH:mm:ss"),
      orderSentBy: { id: userId },
    });
    setOrder(prepared);
    mutation.mutate(prepared);
  }, [order, mutation, userId, bauhofOnly]);

  const handleDelete = useCallback(() => {
    if (order.id === 0) return;
    const deletedOrder = { ...order, deleted: true };
    setOrder(deletedOrder);
    mutation.mutate(deletedOrder);
  }, [order, mutation]);

  const goToDetails = useCallback(() => {
    setEditingSection(null);
    setCurrentStep("DETAILS");
  }, []);

  const applyPatchAndContinue = (patch: Partial<MaterialOrder>, fromStep: WizardStep) => {
    const newOrder: MaterialOrder = { ...order, ...patch };
    setOrder(newOrder);

    if (editingSection) {
      const needsAmount = editingSection === "MATERIAL" && !!newOrder.article && newOrder.amount < 1;
      setEditingSection(needsAmount ? "MENGE" : null);
      setCurrentStep(needsAmount ? "MENGE" : "DETAILS");
      return;
    }

    const isBaustellendienst = newOrder.amount === -1 && !newOrder.article;
    const isBauhof = !!newOrder.article && newOrder.article.bauhof > 0;

    switch (fromStep) {
      case "KIND":
      case "MATERIAL":
      case "BAUHOF": {
        if (expertMode) {
          setCurrentStep("DETAILS");
        } else if (isBauhof) {
          setCurrentStep("SONSTIGES");
        } else if (isBaustellendienst) {
          setCurrentStep("UHRZEIT");
        } else {
          setCurrentStep("MENGE");
        }
        break;
      }
      case "HISTORY":
        setCurrentStep("DETAILS");
        break;
      case "MENGE":
        setCurrentStep("UHRZEIT");
        break;
      case "UHRZEIT":
        setCurrentStep("FUHRPARK");
        break;
      case "FUHRPARK":
        setCurrentStep("SONSTIGES");
        break;
      case "SONSTIGES":
        setCurrentStep("DETAILS");
        break;
      default:
        setCurrentStep("DETAILS");
        break;
    }
  };

  const makeBackHandler = (fallbackStep: WizardStep): (() => void) => {
    if (editingSection) {
      return () => {
        setEditingSection(null);
        setCurrentStep("DETAILS");
      };
    }
    return () => setCurrentStep(fallbackStep);
  };

  const renderStep = () => {
    switch (currentStep) {
      case "KIND":
        return (
          <OrderKindStep
            value={orderKind}
            onChange={(kind) => {
              setOrderKind(kind);
              switch (kind) {
                case "MATERIAL":
                  setCurrentStep("MATERIAL");
                  break;
                case "BAUHOF":
                  setCurrentStep("BAUHOF");
                  break;
                case "HISTORY":
                  setCurrentStep("HISTORY");
                  break;
                case "SITE_WORK":
                  applyPatchAndContinue(
                    {
                      article: null,
                      position: null,
                      amount: -1,
                    },
                    "KIND",
                  );
                  break;
              }
            }}
            canGoBack={!!editingSection}
            onBack={() => {
              if (editingSection) {
                setEditingSection(null);
                setCurrentStep("DETAILS");
              }
            }}
          />
        );

      case "MATERIAL":
        return (
          <MaterialStep
            projectId={projectId}
            order={order}
            isDispo={isDispo}
            onBack={makeBackHandler("KIND")}
            onSelect={(patch) => applyPatchAndContinue(patch, "MATERIAL")}
          />
        );

      case "BAUHOF":
        return (
          <BauhofStep
            projectId={projectId}
            order={order}
            onBack={makeBackHandler("KIND")}
            onSelect={(patch) => applyPatchAndContinue(patch, "BAUHOF")}
          />
        );
      case "HISTORY":
        return (
          <HistoryStep
            projectId={projectId}
            order={order}
            onBack={makeBackHandler("KIND")}
            onSelect={(patch) => applyPatchAndContinue(patch, "HISTORY")}
          />
        );

      case "MENGE":
        return (
          <MengeStep
            isDispo={isDispo}
            order={order}
            onBack={
              editingSection
                ? makeBackHandler("DETAILS")
                : () => {
                    if (orderKind === "MATERIAL") setCurrentStep("MATERIAL");
                    else if (orderKind === "BAUHOF") setCurrentStep("BAUHOF");
                    else setCurrentStep(bauhofOnly ? "BAUHOF" : "KIND");
                  }
            }
            onSelect={(patch) => applyPatchAndContinue(patch, "MENGE")}
          />
        );

      case "UHRZEIT": {
        const isBaustellendienst = order.amount === -1 && !order.article;
        const isBauhof = !!order.article && order.article.bauhof > 0;

        return (
          <UhrzeitStep
            order={order}
            jobId={jobId}
            jobPosition={manualPosition}
            onBack={
              editingSection
                ? makeBackHandler("DETAILS")
                : () => {
                    if (isBaustellendienst) {
                      setCurrentStep(bauhofOnly ? "BAUHOF" : "KIND");
                    } else if (isBauhof) {
                      setCurrentStep("BAUHOF");
                    } else {
                      setCurrentStep("MENGE");
                    }
                  }
            }
            onSelect={(patch) => applyPatchAndContinue(patch, "UHRZEIT")}
          />
        );
      }

      case "FUHRPARK":
        return (
          <FuhrparkStep
            order={order}
            jobPosition={manualPosition}
            jobId={jobId}
            autoCreate={editingSection === null}
            expertMode={expertMode}
            onBack={makeBackHandler("UHRZEIT")}
            onSelect={(patch) => applyPatchAndContinue(patch, "FUHRPARK")}
          />
        );

      case "SONSTIGES":
        return (
          <SonstigesStep
            isDispo={isDispo}
            order={order}
            expertMode={expertMode}
            onBack={makeBackHandler("FUHRPARK")}
            onSelect={(patch) => applyPatchAndContinue(patch, "SONSTIGES")}
          />
        );

      case "DETAILS":
      default:
        return (
          <DetailsStep
            jobPosition={manualPosition}
            isDispo={isDispo}
            order={order}
            setOrder={setOrder}
            mode={mode}
            expertMode={expertMode}
            isSaving={mutation.isPending}
            saveAllowed={!bauhofOnly || isBauhofOrder(order)}
            onSave={handleSave}
            onSend={handleSendNow}
            onDelete={mode === "edit" ? handleDelete : undefined}
            jobId={jobId}
            onEditSection={(section) => {
              setEditingSection(section);
              switch (section) {
                case "MATERIAL":
                  setCurrentStep(bauhofOnly ? "BAUHOF" : "KIND");
                  break;
                case "MATERIAL":
                  setCurrentStep(bauhofOnly ? "BAUHOF" : "KIND");
                  break;
                case "BAUHOF":
                  setCurrentStep("BAUHOF");
                  break;
                case "MENGE":
                  setCurrentStep("MENGE");
                  break;
                case "UHRZEIT":
                  setCurrentStep("UHRZEIT");
                  break;
                case "FUHRPARK":
                  setCurrentStep("FUHRPARK");
                  break;
                case "SONSTIGES":
                  setCurrentStep("SONSTIGES");
                  break;
              }
            }}
          />
        );
    }
  };

  return (
    <Fragment>
      {loading ? <CircularProgress size="large" /> : <Opener onClick={open} />}
      <StyledModal
        open={modalVisible}
        onClose={close}
        onShow={() => setLoading(false)}
        extraButton={
          <Button
            variant="contained"
            startIcon={expertMode ? <Brain /> : <Smile />}
            color={expertMode ? "primary" : "success"}
            onClick={(e) => {
              setSettings({ expertMode: !expertMode });
              e.preventDefault();
              e.stopPropagation();
            }}
            size="small"
          >
            {expertMode ? "Expertenmodus" : "Standardmodus"}
          </Button>
        }
      >
        {bauhofOnly && bauhofDeadline ? <div>Nur Bauhof-Bestellungen sind bis {formatOrderDeadline(bauhofDeadline)} Uhr möglich.</div> : null}
        {renderStep()}
      </StyledModal>
    </Fragment>
  );
}

// Wrap MaterialEditor with the AllowScrollProvider here so it is always wrapped
export default function WrappedMaterialEditor(props: any) {
  return (
    <AllowScrollProvider>
      <MaterialEditor {...props} />
    </AllowScrollProvider>
  );
}
